import React, { FormEvent, useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import { useAuth } from '../contexts/AuthContext';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';
import Footer from '../components/Footer';

const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
const PREMIUM_PRICE_ID = process.env.REACT_APP_STRIPE_PRICE_ID || 'price_1SVBHADUuB1M6RsNLW5m9S7s';
const API_URL = process.env.REACT_APP_API_URL || 'https://app.adsmash.org/api';

// Log configuration on load (for debugging)
console.log('Stripe Configuration:', {
  publishableKey: stripePublishableKey ? stripePublishableKey.substring(0, 20) + '...' : 'not configured',
  priceId: PREMIUM_PRICE_ID,
  apiUrl: API_URL
});

// Only create stripePromise if we have a valid key
const stripePromise = stripePublishableKey && stripePublishableKey.startsWith('pk_') 
  ? loadStripe(stripePublishableKey) 
  : null;

// Payment Form Component with Stripe Elements
const PaymentForm: React.FC<{
  nameOnCard: string;
  email: string;
  zipCode: string;
  fieldErrors: Record<string, string>;
  isSubmitting: boolean;
  error: string | null;
  success: string | null;
  onNameChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  onZipChange: (value: string) => void;
  onError: (error: string) => void;
  onSuccess: () => void;
  onSuccessMessage: (message: string) => void;
  setIsSubmitting: (value: boolean) => void;
  navigate: (path: string) => void;
}> = ({ nameOnCard, email, zipCode, fieldErrors, isSubmitting, error, success, onNameChange, onEmailChange, onZipChange, onError, onSuccess, onSuccessMessage, setIsSubmitting, navigate }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [cardError, setCardError] = useState<string | null>(null);

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#ffffff',
        '::placeholder': {
          color: 'rgba(255, 255, 255, 0.5)',
        },
        iconColor: '#ffffff',
      },
      invalid: {
        color: '#ef4444',
        iconColor: '#ef4444',
      },
    },
    hidePostalCode: true, // We'll use zipCode field separately
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setCardError(null);
    onError('');

    if (!stripe || !elements) {
      onError('Stripe has not loaded yet. Please wait and try again.');
      return;
    }

    setIsSubmitting(true);

    try {
      // If user is not logged in, check if email is already registered before card tokenization
      const token = localStorage.getItem('token');
      if (!token) {
        try {
          const checkRes = await axios.post(`${API_URL}/subscription/check-email`, { email: email.trim() });
          if (checkRes.data?.registered) {
            onError('Email is already registered. Please log in.');
            setIsSubmitting(false);
            return;
          }
        } catch (checkErr) {
          // If check endpoint fails, fallback to backend transaction validation
        }
      }

      // Create payment method from card element using Elements API
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        elements,
        params: {
          billing_details: {
            name: nameOnCard,
            email: email,
            address: {
              postal_code: zipCode,
            },
          },
        },
      });

      if (pmError || !paymentMethod) {
        throw new Error(pmError?.message || 'Failed to create payment method');
      }

      // Log payment method creation for debugging
      console.log('Payment method created:', {
        id: paymentMethod.id,
        type: paymentMethod.type,
        created: new Date().toISOString()
      });

      // If user is not logged in, process payment as guest first
      // IMPORTANT: Use payment method immediately after creation to avoid expiration
      if (!token) {
        try {
          console.log('Sending payment method to backend:', {
            paymentMethodId: paymentMethod.id,
            priceId: PREMIUM_PRICE_ID,
            email: email.trim(),
            timestamp: new Date().toISOString()
          });

          const guestResponse = await axios.post(
            `${API_URL}/subscription/create-guest-subscription`,
            {
              paymentMethodId: paymentMethod.id,
              priceId: PREMIUM_PRICE_ID,
              email: email.trim(),
              name: nameOnCard.trim() || 'User',
            },
            {
              timeout: 30000, // 30 second timeout
            }
          );

          console.log('Guest subscription response:', guestResponse.data);

          if (!guestResponse.data.success) {
            throw new Error(guestResponse.data.error || 'Failed to process payment. Please try again.');
          }

          if (guestResponse.data.requiresAction && guestResponse.data.clientSecret) {
            const { error: confirmGuestError } = await stripe.confirmCardPayment(guestResponse.data.clientSecret);
            if (confirmGuestError) {
              throw new Error(confirmGuestError.message || 'Payment authentication failed');
            }
          }

          // Payment successful for guest
          onSuccessMessage('Payment successful! Redirecting to create your account...');
          setIsSubmitting(false);

          // Store guest subscription details for account creation flow
          sessionStorage.setItem('guest_subscription_id', guestResponse.data.subscriptionId);
          sessionStorage.setItem('guest_customer_id', guestResponse.data.customerId);
          sessionStorage.setItem('pending_signup_email', email.trim());
          sessionStorage.setItem('pending_signup_name', nameOnCard.trim() || 'User');
          sessionStorage.setItem('pending_price_id', PREMIUM_PRICE_ID);

          // Redirect to create password page after showing success message
          setTimeout(() => {
            window.location.href = '/create-password';
          }, 2000);
          return;
        } catch (guestError: any) {
          // If payment method expired, show helpful error
          if (guestError.response?.data?.error?.includes('PaymentMethod') || 
              guestError.response?.data?.error?.includes('payment method')) {
            throw new Error('Your card details have expired. Please re-enter your card information and try again.');
          }
          throw guestError;
        }
      }

      // User is logged in - proceed with payment
      // IMPORTANT: Use payment method immediately to avoid expiration
      console.log('Sending payment method to backend (logged in user):', {
        paymentMethodId: paymentMethod.id,
        priceId: PREMIUM_PRICE_ID,
        timestamp: new Date().toISOString()
      });

      const response = await axios.post(
        `${API_URL}/subscription/create-subscription`,
        {
          paymentMethodId: paymentMethod.id,
          priceId: PREMIUM_PRICE_ID,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          timeout: 30000, // 30 second timeout
        }
      );

      console.log('Subscription response:', response.data);

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to create subscription');
      }

      // Handle 3D Secure if required
      if (response.data.requiresAction && response.data.clientSecret) {
        const { error: confirmError } = await stripe.confirmCardPayment(response.data.clientSecret);
        
        if (confirmError) {
          throw new Error(confirmError.message || 'Payment authentication failed');
        }
      }

      // Check if payment was successful
      if (response.data.success && response.data.subscription) {
        const subscriptionStatus = response.data.subscription.status;
        
        if (subscriptionStatus === 'active' || subscriptionStatus === 'trialing') {
          // Payment successful - update localStorage immediately
          localStorage.setItem('subscription_status', 'active');
          localStorage.setItem('subscription_plan', response.data.subscription.plan || 'PREMIUM');
          
          console.log('Payment successful - User subscription updated:', {
            status: 'active',
            plan: response.data.subscription.plan || 'PREMIUM'
          });
          
          // Payment successful - show success message
          onSuccessMessage('Payment successful! Your premium membership has been activated.');
          setIsSubmitting(false);
          
          // Store email and redirect after 2 seconds
          sessionStorage.setItem('pending_payment_email', email.trim());
          
          setTimeout(() => {
            onSuccess();
            navigate('/payment-success');
          }, 2000);
          return;
        } else {
          throw new Error(`Payment processed but subscription status is: ${subscriptionStatus}. Please contact support.`);
        }
      } else {
        throw new Error('Payment was processed but subscription was not created. Please contact support.');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'An error occurred during payment';
      console.error('Payment error:', err);
      onError(errorMessage);
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <input
          type="text"
          value={nameOnCard}
          onChange={(event) => onNameChange(event.target.value)}
          placeholder="Name On Card"
          className={`w-full rounded-lg border px-4 py-3 bg-white/5 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition ${
            fieldErrors.nameOnCard ? 'border-red-400/50' : 'border-white/20'
          }`}
          required
        />
        {fieldErrors.nameOnCard && (
          <p className="mt-1 text-xs text-red-400">{fieldErrors.nameOnCard}</p>
        )}
      </div>

      {/* Stripe Card Element */}
      <div>
        <label className="block text-white/70 text-sm mb-2">Card Details</label>
        <div className={`rounded-lg border px-4 py-3 bg-white/5 transition ${
          cardError ? 'border-red-400/50' : 'border-white/20'
        }`}>
          <CardElement 
            options={cardElementOptions}
            onChange={(e: any) => {
              if (e.error) {
                setCardError(e.error.message);
              } else {
                setCardError(null);
              }
            }}
          />
        </div>
        {cardError && (
          <p className="mt-1 text-xs text-red-400">{cardError}</p>
        )}
      </div>

      <div>
        <input
          type="text"
          value={zipCode}
          onChange={(event) => onZipChange(event.target.value)}
          placeholder="Zip Code (5 digits)"
          maxLength={5}
          className={`w-full rounded-lg border px-4 py-3 bg-white/5 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition ${
            fieldErrors.zipCode ? 'border-red-400/50' : 'border-white/20'
          }`}
          required
        />
        {fieldErrors.zipCode && (
          <p className="mt-1 text-xs text-red-400">{fieldErrors.zipCode}</p>
        )}
      </div>

      <div>
        <input
          type="email"
          value={email}
          onChange={(event) => onEmailChange(event.target.value)}
          placeholder="Email Address"
          className={`w-full rounded-lg border px-4 py-3 bg-white/5 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition ${
            fieldErrors.email ? 'border-red-400/50' : 'border-white/20'
          }`}
          required
        />
        {fieldErrors.email && (
          <p className="mt-1 text-xs text-red-400">{fieldErrors.email}</p>
        )}
      </div>

      {/* Terms Text */}
      <p className="text-xs text-white/60 leading-relaxed">
        By clicking 'Buy now' you agree to our terms, privacy policy and to pay $29 today which includes a 70% discount for your first term, then auto renews annually for <span className="line-through">$99</span> unless you cancel via your online account.
      </p>

      {error && (
        <div className="rounded-lg border border-red-400/50 bg-red-500/20 px-4 py-3 text-sm text-red-300">
          {error}
          {(error.toLowerCase().includes('already registered') || error.toLowerCase().includes('log in')) && (
            <Link to="/login" className="underline font-semibold ml-2 text-primary-300 hover:text-primary-200">
              Log in here →
            </Link>
          )}
        </div>
      )}

      {success && (
        <div className="rounded-lg border border-emerald-400/50 bg-emerald-500/20 px-4 py-3 text-sm text-emerald-300 flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
          </svg>
          {success}
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || isSubmitting}
        className="btn-adsmash-primary w-full inline-flex items-center justify-center rounded-lg px-6 py-4 font-semibold text-white shadow-lg transition hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {isSubmitting ? 'Processing Payment...' : 'Buy now - $29 USD'}
      </button>
    </form>
  );
};

// PayPal Payment Form Component
const PayPalPaymentForm: React.FC<{
  nameOnCard: string;
  email: string;
  zipCode: string;
  fieldErrors: Record<string, string>;
  isSubmitting: boolean;
  error: string | null;
  success: string | null;
  onNameChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  onZipChange: (value: string) => void;
  onError: (error: string) => void;
  onSuccess: () => void;
  onSuccessMessage: (message: string) => void;
  setIsSubmitting: (value: boolean) => void;
  navigate: (path: string) => void;
}> = ({ nameOnCard, email, zipCode, fieldErrors, isSubmitting, error, success, onNameChange, onEmailChange, onZipChange, onError, onSuccess, onSuccessMessage, setIsSubmitting, navigate }) => {
  const handlePayPalCheckout = async () => {
    // Validate required fields
    if (!email || !email.trim()) {
      onError('Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      onError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    onError('');

    try {
      const token = localStorage.getItem('token');
      
      // If guest checkout, check if email is already registered before opening PayPal popup
      if (!token) {
        try {
          const checkRes = await axios.post(`${API_URL}/subscription/check-email`, { email: email.trim() });
          if (checkRes.data?.registered) {
            onError('Email is already registered. Please log in.');
            setIsSubmitting(false);
            return;
          }
        } catch (checkErr) {
          // If check endpoint fails, fallback to backend transaction validation
        }
      }

      // Create PayPal order
      const response = await axios.post(
        `${API_URL}/subscription/create-paypal-order`,
        {
          email: email.trim(),
          name: nameOnCard.trim() || 'User',
        },
        {
          headers: token ? {
            Authorization: `Bearer ${token}`,
          } : {},
          timeout: 30000,
        }
      );

      if (!response.data.success || !response.data.approvalUrl) {
        throw new Error(response.data.error || 'Failed to create PayPal order');
      }

      // Store PayPal order ID for later capture
      if (response.data.orderId) {
        sessionStorage.setItem('paypal_order_id', response.data.orderId);
      }

      // Open PayPal in a new tab/window
      const paypalWindow = window.open(
        response.data.approvalUrl,
        '_blank',
        'noopener,noreferrer'
      );

      // Check if popup was blocked
      if (!paypalWindow || paypalWindow.closed || typeof paypalWindow.closed === 'undefined') {
        setIsSubmitting(false);
        onError('Popup blocked. Please allow popups for this site and try again.');
        return;
      }

      // Reset submitting state since we opened in new tab
      setIsSubmitting(false);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'An error occurred during PayPal checkout';
      console.error('PayPal checkout error:', err);
      onError(errorMessage);
      setIsSubmitting(false);
    }
  };

  const validateEmail = (value: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  };

  return (
    <div className="space-y-4">
      <div>
        <input
          type="text"
          value={nameOnCard}
          onChange={(event) => onNameChange(event.target.value)}
          placeholder="Full Name"
          className={`w-full rounded-lg border px-4 py-3 bg-white/5 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition ${
            fieldErrors.nameOnCard ? 'border-red-400/50' : 'border-white/20'
          }`}
          required
        />
        {fieldErrors.nameOnCard && (
          <p className="mt-1 text-xs text-red-400">{fieldErrors.nameOnCard}</p>
        )}
      </div>

      <div>
        <input
          type="email"
          value={email}
          onChange={(event) => onEmailChange(event.target.value)}
          placeholder="Email Address"
          className={`w-full rounded-lg border px-4 py-3 bg-white/5 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition ${
            fieldErrors.email ? 'border-red-400/50' : 'border-white/20'
          }`}
          required
        />
        {fieldErrors.email && (
          <p className="mt-1 text-xs text-red-400">{fieldErrors.email}</p>
        )}
      </div>

      {/* Terms Text */}
      <p className="text-xs text-white/60 leading-relaxed">
        By clicking 'Pay with PayPal' you agree to our terms, privacy policy and to pay $29 today which includes a 70% discount for your first term, then auto renews annually for <span className="line-through">$99</span> unless you cancel via your online account.
      </p>

      {error && (
        <div className="rounded-lg border border-red-400/50 bg-red-500/20 px-4 py-3 text-sm text-red-300">
          {error}
          {(error.toLowerCase().includes('already registered') || error.toLowerCase().includes('log in')) && (
            <Link to="/login" className="underline font-semibold ml-2 text-primary-300 hover:text-primary-200">
              Log in here →
            </Link>
          )}
        </div>
      )}

      {success && (
        <div className="rounded-lg border border-emerald-400/50 bg-emerald-500/20 px-4 py-3 text-sm text-emerald-300 flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
          </svg>
          {success}
        </div>
      )}

      <button
        type="button"
        onClick={handlePayPalCheckout}
        disabled={isSubmitting || !email}
        className="btn-adsmash-primary w-full inline-flex items-center justify-center rounded-lg px-6 py-4 font-semibold text-white shadow-lg transition hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
        style={{
          background: 'linear-gradient(135deg, #ffbc2f 0%, #ff8000 100%)',
        }}
      >
        {isSubmitting ? (
          <span className="flex items-center gap-2">
            <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Opening PayPal...
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <img src="/images/paypal.png" alt="PayPal" className="h-5" />
            Pay with PayPal - $29 USD
          </span>
        )}
      </button>
    </div>
  );
};

// Payment Section Component (must be inside Elements)
const PaymentSection: React.FC<{
  paymentMethod: 'card' | 'paypal';
  setPaymentMethod: (method: 'card' | 'paypal') => void;
  nameOnCard: string;
  email: string;
  zipCode: string;
  fieldErrors: Record<string, string>;
  isSubmitting: boolean;
  error: string | null;
  success: string | null;
  stripeLoaded: boolean;
  onNameChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  onZipChange: (value: string) => void;
  onError: (error: string) => void;
  onSuccess: () => void;
  onSuccessMessage: (message: string) => void;
  setIsSubmitting: (value: boolean) => void;
  navigate: (path: string) => void;
}> = ({ paymentMethod, setPaymentMethod, nameOnCard, email, zipCode, fieldErrors, isSubmitting, error, success, stripeLoaded, onNameChange, onEmailChange, onZipChange, onError, onSuccess, onSuccessMessage, setIsSubmitting, navigate }) => {
  return (
    <section 
      className="p-8 rounded-lg"
      style={{ backgroundColor: 'rgb(0 0 0 / 28%)' }}
    >
      {/* Payment Method Selection */}
      <div className="flex flex-col gap-4 sm:flex-row mb-6">
        <button
          type="button"
          onClick={() => setPaymentMethod('card')}
          className={`w-full sm:flex-1 flex items-center gap-3 px-4 py-3 rounded-lg border-2 transition-all ${
            paymentMethod === 'card'
              ? 'border-[#00a3d9]'
              : 'border-white/20 hover:border-white/30'
          }`}
          style={{
            background: 'radial-gradient(circle, rgb(0 163 217 / 23%), rgb(0 119 182 / 46%))',
            borderColor: paymentMethod === 'card' ? '#00a3d9' : 'rgba(255, 255, 255, 0.2)'
          }}
        >
          {/* Radio Button Indicator */}
          <div className="flex-shrink-0">
            {paymentMethod === 'card' ? (
              <div className="w-5 h-5 rounded-full border-2 border-white flex items-center justify-center">
                <div className="w-2 h-2 rounded-full bg-red-500"></div>
              </div>
            ) : (
              <div className="w-5 h-5 rounded-full border-2 border-white/50"></div>
            )}
          </div>
          
          {/* Credit Card Icon */}
          <svg className="w-6 h-6 text-white flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
          </svg>
          
          <span className="font-medium text-white">Credit Card</span>
        </button>
        <button
          type="button"
          onClick={() => setPaymentMethod('paypal')}
          className={`w-full sm:flex-1 flex items-center gap-3 px-4 py-3 rounded-lg border-2 transition-all whitespace-nowrap ${
            paymentMethod === 'paypal'
              ? 'border-orange-500'
              : 'border-white/20 hover:border-white/30'
          }`}
          style={{
            background: 'radial-gradient(circle, #ffbc2f, #ff8000)',
            borderColor: paymentMethod === 'paypal' ? '#ff8000' : 'rgba(255, 255, 255, 0.2)'
          }}
        >
          {/* Radio Button Indicator */}
          <div className="flex-shrink-0">
            {paymentMethod === 'paypal' ? (
              <div className="w-5 h-5 rounded-full border-2 border-white flex items-center justify-center">
                <div className="w-2 h-2 rounded-full bg-red-500"></div>
              </div>
            ) : (
              <div className="w-5 h-5 rounded-full border-2 border-white/50"></div>
            )}
          </div>
          
          <span className="font-medium text-white whitespace-nowrap">Pay With</span>
          <img src="/images/paypal.png" alt="PayPal" className="h-4 flex-shrink-0" />
        </button>
      </div>

      {paymentMethod === 'card' ? (
        stripeLoaded ? (
          <PaymentForm
            nameOnCard={nameOnCard}
            email={email}
            zipCode={zipCode}
            fieldErrors={fieldErrors}
            isSubmitting={isSubmitting}
            error={error}
            success={success}
            onNameChange={onNameChange}
            onEmailChange={onEmailChange}
            onZipChange={onZipChange}
            onError={onError}
            onSuccess={onSuccess}
            onSuccessMessage={onSuccessMessage}
            setIsSubmitting={setIsSubmitting}
            navigate={navigate}
          />
        ) : (
          <div className="bg-blue-50/10 border border-blue-200/30 rounded-lg p-4 text-center">
            <p className="text-white/70">Loading payment form...</p>
          </div>
        )
      ) : (
        <PayPalPaymentForm
          nameOnCard={nameOnCard}
          email={email}
          zipCode={zipCode}
          fieldErrors={fieldErrors}
          isSubmitting={isSubmitting}
          error={error}
          success={success}
          onNameChange={onNameChange}
          onEmailChange={onEmailChange}
          onZipChange={onZipChange}
          onError={onError}
          onSuccess={onSuccess}
          onSuccessMessage={onSuccessMessage}
          setIsSubmitting={setIsSubmitting}
          navigate={navigate}
        />
      )}
    </section>
  );
};

const Payment: React.FC = () => {
  const { user: userContextUser } = useContext(UserContext);
  const { user: authUser } = useAuth();
  const currentUser = userContextUser || authUser;
  
  const [nameOnCard, setNameOnCard] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [email, setEmail] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'paypal'>('card');
  const isPayPalDisabled = true;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [stripeLoaded, setStripeLoaded] = useState(false);
  const navigate = useNavigate();

  // Check if Stripe is loaded
  useEffect(() => {
    if (stripePromise) {
      stripePromise
        .then((stripe) => {
          if (stripe) {
            setStripeLoaded(true);
          } else {
            setStripeLoaded(false);
            setError('Failed to initialize Stripe. Please refresh the page.');
          }
        })
        .catch((err) => {
          console.error('Stripe loading error:', err);
          setStripeLoaded(false);
          setError('Failed to load Stripe. Please refresh the page.');
        });
    } else {
      setStripeLoaded(false);
      setError('Stripe is not configured. Please contact support.');
    }
  }, []);

  // Auto-fill email and name if user is logged in
  useEffect(() => {
    if (currentUser) {
      // Set email if not already set
      if (currentUser.email) {
        setEmail(prev => prev || currentUser.email);
      }
      
      // Set full name (firstName + lastName) if not already set
      if (currentUser.firstName || currentUser.lastName) {
        const fullName = `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim();
        if (fullName) {
          setNameOnCard(prev => prev || fullName);
        }
      }
    }
  }, [currentUser]);

  // Validate zip code (exactly 5 digits)
  const validateZipCode = (value: string): boolean => {
    const cleaned = value.replace(/\D/g, '');
    return /^\d{5}$/.test(cleaned);
  };

  // Validate email format
  const validateEmail = (value: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  };

  // Handle zip code change (exactly 5 digits)
  const handleZipCodeChange = (value: string) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 5); // Only allow 5 digits
    setZipCode(cleaned);
    if (cleaned.length > 0) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        if (cleaned.length < 5) {
          newErrors.zipCode = 'Zip code must be exactly 5 digits';
        } else if (!validateZipCode(cleaned)) {
          newErrors.zipCode = 'Please enter a valid zip code (5 digits)';
        } else {
          delete newErrors.zipCode;
        }
        return newErrors;
      });
    } else {
      setFieldErrors(prev => {
        const { zipCode, ...rest } = prev;
        return rest;
      });
    }
  };

  // Handle email change
  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (value.length > 0) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        if (!validateEmail(value)) {
          newErrors.email = 'Please enter a valid email address';
        } else {
          delete newErrors.email;
        }
        return newErrors;
      });
    } else {
      setFieldErrors(prev => {
        const { email, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleSuccess = () => {
    navigate('/payment/success');
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: 'linear-gradient(to bottom, #0c0d1f, #1b1324)' }}
    >
      <header className="w-full px-6 md:px-12 lg:px-20 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img src="/images/logo.png" alt="AdSmash Logo" className="h-10 md:h-12" />
        </Link>
        <Link
          to="/"
          className="text-sm text-white/70 hover:text-white transition-colors"
        >
          Back to home
        </Link>
      </header>

      <main className="flex-1 px-6 md:px-12 lg:px-20 pb-12">
        <div className="max-w-6xl mx-auto">
          {/* Header Section */}
          <div className="mb-10 text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Get AdSmash 70% off.</h1>
            <p className="text-xl text-white/80">70% introductory discount: just $2/month.</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Product Details */}
            <section className="space-y-6">
              {/* Wrapped Section with Background */}
              <div 
                className="rounded-lg px-6 pt-3 pb-6 space-y-6 relative"
                style={{ backgroundColor: 'rgb(0 0 0 / 28%)' }}
              >
                {/* 70% OFF Badge - Sticker in top right */}
                <div 
                  className="absolute right-4 bg-blue-400 text-white px-6 py-2 rounded-full font-bold shadow-lg z-10"
                  style={{ top: '-25px' }}
                >
                  70% OFF!
                </div>
                
                {/* AdSmash Branding - Single Row */}
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <img src="/images/logo.png" alt="AdSmash Logo" className="h-10 md:h-12" />
                  
                  {/* Pricing */}
                  <div className="flex items-baseline gap-3">
                    <span className="text-white/50 line-through ">$99/year</span>
                    <span className="text-primary-500 text-2xl font-bold">$29/year</span>
                  </div>
                </div>

                {/* Block ads everywhere section */}
                <div>
                  <h3 className="text-white font-semibold mb-4" style={{ fontSize: '1.125rem' }}>
                    Block ads everywhere:
                  </h3>
                  <img 
                    src="/images/websites.png" 
                    alt="Supported websites" 
                    className="w-full h-auto"
                  />
                </div>

                {/* Features List */}
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-white">
                    <img 
                      src="/images/tick.png" 
                      alt="Checkmark" 
                      className="flex-shrink-0 mt-0.5"
                      style={{ width: '1rem', height: '1rem' }}
                    />
                    <span>Easy + fast installation (60 seconds or less).</span>
                  </li>
                  <li className="flex items-start gap-3 text-white">
                    <img 
                      src="/images/tick.png" 
                      alt="Checkmark" 
                      className="flex-shrink-0 mt-0.5"
                      style={{ width: '1rem', height: '1rem' }}
                    />
                    <span>Block nearly all ads instantly.</span>
                  </li>
                  <li className="flex items-start gap-3 text-white">
                    <img 
                      src="/images/tick.png" 
                      alt="Checkmark" 
                      className="flex-shrink-0 mt-0.5"
                      style={{ width: '1rem', height: '1rem' }}
                    />
                    <span>Load pages up to 2x faster.</span>
                  </li>
                </ul>
              </div>

              {/* Pricing Breakdown */}
              <div className=" p-4 space-y-2">
                <div className="flex justify-between text-white">
                  <span>Regular yearly rate</span>
                  <span className="line-through">$99</span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: '#48d1ff' }}>Your price (70% off for the first year)</span>
                  <span className="text-white font-semibold">$29</span>
                </div>
              </div>

              {/* 100% Money Back Guarantee */}
              <div className="flex items-start gap-3  p-4">
                <img 
                  src="/images/g-badge.png" 
                  alt="Money back guarantee badge" 
                  className="flex-shrink-0"
                  style={{ width: '4rem', height: '5rem' }}
                />
                <div>
                  <h4 className="text-white font-semibold mb-1">100% Money Back Guarantee</h4>
                  <p className="text-white/70 text-sm">
                    Don't like AdSmash for any reason? Just let us know within 30 days for a full refund.
                  </p>
                </div>
              </div>
            </section>

            {/* Right Column - Payment Form */}
            {stripePromise && stripeLoaded ? (
              <Elements stripe={stripePromise}>
                <PaymentSection
                  paymentMethod={paymentMethod}
                  setPaymentMethod={setPaymentMethod}
                  nameOnCard={nameOnCard}
                  email={email}
                  zipCode={zipCode}
                  fieldErrors={fieldErrors}
                  isSubmitting={isSubmitting}
                  error={error}
                  success={success}
                  stripeLoaded={stripeLoaded}
                  onNameChange={setNameOnCard}
                  onEmailChange={handleEmailChange}
                  onZipChange={handleZipCodeChange}
                  onError={setError}
                  onSuccess={handleSuccess}
                  onSuccessMessage={setSuccess}
                  setIsSubmitting={setIsSubmitting}
                  navigate={navigate}
                />
              </Elements>
            ) : stripePromise && !stripeLoaded ? (
              <section 
                className="p-8 rounded-lg"
                style={{ backgroundColor: 'rgb(0 0 0 / 28%)' }}
              >
                <div className="bg-blue-50/10 border border-blue-200/30 rounded-lg p-4 text-center">
                  <p className="text-white/70">Loading payment form...</p>
                </div>
              </section>
            ) : (
              <section 
                className="p-8 rounded-lg"
                style={{ backgroundColor: 'rgb(0 0 0 / 28%)' }}
              >
                <div className="bg-red-50/10 border border-red-200/30 rounded-lg p-4 text-center">
                  <p className="text-white/70">Stripe is not configured. Please contact support.</p>
                </div>
              </section>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Payment;
